/**
 * CF-216 §6 — the single visibility authority for the balance surface.
 *
 * Governing design: plans/CF-216-SEC6-VISIBILITY-DESIGN-V6.1.md
 *   sha256 dee2c24946944148833bf233d7d04f42c57c50e673522de58e44d57e2d3e7500
 *
 * ── WHY THIS IS A PURE MODULE AND NOT INLINE JSX ────────────────────────────
 * This repo has NO DOM test runner (see package.json "test": two i18n/variant
 * scripts; no jsdom, no vitest, no jest). A visibility rule that lives inside JSX
 * therefore cannot be asserted by anything at all — which is exactly how the
 * establishing row at App.tsx:976 stayed dead for ~4 months:
 *
 *     hasUnknownPositiveBalance = !balanceLoaded && balNano !== null && balNano > 0n
 *
 * is UNSATISFIABLE, because `balNano` derives from `depositBalance`, which is null
 * whenever `!balanceLoaded`. No test could observe it and no reader noticed. Making
 * the decision a pure function is what makes the 17 required cases assertable at all.
 * Same idiom as coinflip-mode.ts: pure + render-free, no React, no DOM, no network.
 *
 * ── THE ONE INVARIANT THIS MODULE EXISTS TO ENFORCE ─────────────────────────
 * ⛔ UNKNOWN IS NEVER RENDERED AS ABSENT.
 *   - A `0` may be produced ONLY from CashDisplay.kind === 'known'. The other two
 *     cash kinds carry NO numeric field, so a renderer physically cannot read a 0
 *     out of ignorance.
 *   - A hidden chips row may be produced ONLY from a CONFIRMED negative (`none` or
 *     `terminal`). Ignorance maps to 'establishing' or 'unavailable', never 'hidden'.
 *
 * ── WHAT THIS MODULE DELIBERATELY DOES NOT DO ──────────────────────────────
 * It decides VISIBILITY only. It is not a permission gate. The real-money gates
 * (balance freshness, affordability, tier_config presence) stay at their call sites
 * UNCHANGED, per coinflip-mode.ts's design-align (c) and redscreen-walls R6.
 * `actionsEnabled` below is a necessary condition, never a sufficient one.
 */

import type { BalanceState } from '../App';
import type { BonusView } from './bonus-state.ts';
import { deriveCoinflipMode, type CoinflipMode } from './coinflip-mode.ts';

/** Cash, as the surface is allowed to see it. Only `known` carries a number. */
export type CashDisplay =
    | { kind: 'known'; nano: bigint }
    | { kind: 'establishing' }
    | { kind: 'unavailable'; code: string };

/** Bonus chips, as the surface is allowed to see it. Only `amount` carries a number. */
export type ChipsDisplay =
    | { kind: 'amount'; nano: bigint }
    | { kind: 'establishing' }
    | { kind: 'unavailable' }
    | { kind: 'hidden' };

export interface BalanceSurface {
    cash: CashDisplay;
    chips: ChipsDisplay;
    mode: CoinflipMode;
    /** Genuine practice experience: restore complete, no wallet connected. */
    practiceDemo: boolean;
    /** Connected, cash KNOWN to be exactly zero. Not practice — a real user at 0. */
    connectedNoCash: boolean;
    /** Whether the chips row occupies space at all. */
    chipsRowVisible: boolean;
    /**
     * CF-BALANCE-VISIBILITY-V1 Surface A — whether the wallet menu's money row exists.
     *
     * ⛔ CORRECTED 2026-08-21. The text that stood here said a "playable" headline would assert
     * something the app does not honour, because "5 cash + 5 chips would read as 10 while an 8 TON
     * stake stays disabled". That described a CLIENT DEFECT as if it were a system law, and it was
     * wrong: `bonus-engine.ts:484-485` funds gross burn-first (`chipGross = min(sticky, gross)`)
     * and `ledger.ts:199` refuses only when the CASH remainder exceeds cash. 5 cash + 5 chips
     * against an 8 gross resolves to chip 5 / cash 3 and SUCCEEDS. The ledger's true ceiling is
     * cash + chips; the stake stayed disabled because `CoinFlip.tsx` gated on cash alone.
     *
     * ⭐ I reasoned from this comment instead of from the ledger, and defended "no sum" for hours
     * on the strength of it. A durable comment outlives the belief that produced it — which is
     * exactly why a wrong one is more expensive than no comment.
     *
     * This row still renders cash and chips as two labelled facts with no aggregate, because that
     * is what Surface A is for. The flip-screen headline renders the playable total (PL #13704).
     *
     * The row earns its space when there is something TRUE to say: cash is known (0 included — a
     * connected player at zero needs to see the zero beside their chips, or the chips read as the
     * whole balance), or the chips column is visible. Cash-unknown with no chips is already served
     * by `walletMenuCashStatus === 'checking'`, so this predicate opens no silence.
     */
    walletMoneyRowVisible: boolean;
    /** NECESSARY condition for money actions. Never sufficient — see file header. */
    actionsEnabled: boolean;
    /** Surface B (wallet menu). 'checking' renders app.wallet.checking_session. */
    walletMenuCashStatus: 'checking' | 'none';
}

/**
 * `loaded` is only ever constructed by parseBalanceDto, which validates balance_nano
 * against /^[0-9]+$/ before admitting it. So the catch below is unreachable by
 * construction. It is NOT a fallback that hides a problem: it fails CLOSED to
 * `unavailable`, which can never render a number, rather than letting a malformed
 * value reach BigInt() and take the surface down. If it ever fires, the contract
 * between the parser and this module has been broken and the UI says so.
 */
function nanoOf(raw: string): bigint | null {
    try { return BigInt(raw); } catch { return null; }
}

export function cashDisplay(b: BalanceState | undefined): CashDisplay {
    if (!b) return { kind: 'establishing' };
    switch (b.kind) {
        case 'loading': return { kind: 'establishing' };
        case 'error': return { kind: 'unavailable', code: b.code };
        case 'loaded': {
            const nano = nanoOf(b.balance_nano);
            return nano === null
                ? { kind: 'unavailable', code: 'nano_unparseable' }
                : { kind: 'known', nano };
        }
    }
}

/**
 * ⛔ The asymmetry here is the whole point. `none` and `terminal` are the server
 * SAYING there is nothing — those hide. `unavailable` and pre-bootstrap are us not
 * KNOWING — those get their own copy. bonus-state.ts already documents the cost of
 * conflating them: "a player whose first read never succeeds sees silence forever —
 * which is indistinguishable from 'you have no bonus'."
 */
export function chipsDisplay(
    view: BonusView,
    bootstrapped: boolean,
    initialReadExhausted: boolean,
): ChipsDisplay {
    // Before the first successful answer the view is meaningless, whatever it holds.
    if (!bootstrapped) {
        return initialReadExhausted ? { kind: 'unavailable' } : { kind: 'establishing' };
    }
    switch (view.kind) {
        case 'active': {
            const nano = nanoOf(view.stickyNano);
            if (nano === null) return { kind: 'unavailable' };
            return nano > 0n ? { kind: 'amount', nano } : { kind: 'hidden' };
        }
        case 'none':        return { kind: 'hidden' };   // CONFIRMED negative
        case 'terminal':    return { kind: 'hidden' };   // converted / expired / voided
        case 'unavailable': return { kind: 'unavailable' };
    }
}

export interface BalanceSurfaceInput {
    balanceState: BalanceState | undefined;
    bonusView: BonusView;
    bonusBootstrapped: boolean;
    bonusInitialReadExhausted: boolean;
    isConnected: boolean;
    connectionRestored: boolean;
}

/**
 * CF-AFFORDABILITY-V1 — the single frontend answer to "how much gross can this player fund?"
 *
 * Mirrors the ledger: chips burn first, cash covers the remainder, so the ceiling is cash + chips
 * (`bonus-engine.ts:484-485`, `ledger.ts:199`).
 *
 * ⛔ ONLY `amount` WITH A POSITIVE VALUE CONTRIBUTES. Everything else contributes zero, but for
 *    two DIFFERENT reasons and the distinction matters for copy and tests:
 *      · `establishing` / `unavailable` = UNKNOWN. We have not read it. Degrading to cash-only
 *        under-serves the player briefly rather than offering a stake the ledger may refuse.
 *      · `hidden` = CONFIRMED nothing to spend (server-confirmed none, terminal, or active zero).
 *    ⚠️ Corrected 18:07Z — an earlier revision of this comment called `hidden` unknown. It is not,
 *    and collapsing "confirmed none" into "not yet read" is exactly the class of imprecision that
 *    put a client bug in a comment and cost me half a day (see the CORRECTED note above).
 *
 * ⚠️ CONSEQUENCE, stated so it is not mistaken for a bug: affordability WIDENS when a late bonus
 *    read lands. A stake can be disabled at t=0 and enabled at t=1 with no user action.
 */
export function availableGrossNano(cash: CashDisplay, chips: ChipsDisplay): bigint {
    const cashNano = cash.kind === 'known' ? cash.nano : 0n;
    const chipsNano = chips.kind === 'amount' && chips.nano > 0n ? chips.nano : 0n;
    return cashNano + chipsNano;
}

/** True only when chips are CONFIRMED to be contributing — the suppression predicate. */
export function chipsAreFunding(chips: ChipsDisplay): boolean {
    return chips.kind === 'amount' && chips.nano > 0n;
}

export function balanceSurface(input: BalanceSurfaceInput): BalanceSurface {
    const cash = cashDisplay(input.balanceState);
    const chips = chipsDisplay(
        input.bonusView, input.bonusBootstrapped, input.bonusInitialReadExhausted,
    );

    // ⛔ deriveCoinflipMode's three-value contract is NOT modified (it guards
    // CF-DEMO-BALANCE-FLASH-FIX-V1). We only feed it the union-derived hasDeposit,
    // which is the same predicate CoinFlip.tsx:566 already computes.
    const hasDeposit = cash.kind === 'known' && cash.nano > 0n;
    const mode = deriveCoinflipMode(input.connectionRestored, input.isConnected, hasDeposit);

    const practiceDemo = mode === 'demo' && !input.isConnected;
    const connectedNoCash =
        mode === 'demo' && input.isConnected && cash.kind === 'known' && cash.nano === 0n;

    // Practice shows a FAKE balance; hanging real bonus chips beside it would be a lie.
    // `restoring` paints a neutral "Connecting…" surface with no numbers at all.
    const chipsRowVisible =
        !practiceDemo && mode !== 'restoring' && chips.kind !== 'hidden';

    return {
        cash,
        chips,
        mode,
        practiceDemo,
        connectedNoCash,
        chipsRowVisible,
        walletMoneyRowVisible: cash.kind === 'known' || chipsRowVisible,
        actionsEnabled: cash.kind === 'known',
        walletMenuCashStatus: cash.kind === 'establishing' ? 'checking' : 'none',
    };
}
